/**
 * Request budget for the reads every fresh load makes.
 *
 * Measured on staging (2026-10-01): a fresh authenticated load lands on
 * /dashboard and BeHeard's API received 7 calls, then 6 (next-step derived
 * from the path). It is now 4 at first paint: the path rides on the guard's
 * GET /onboarding/me?include=path, and GET /me/public-profile mounts only
 * when the readiness card nears the viewport (5 after scrolling to it), or at
 * once when the readiness route depends on it. The server's per-IP limiter
 * (100 / 15 min) is shared by everyone behind one address, so these reads are
 * worth keeping cheap. This module changes HOW LONG low-churn reads stay cached
 * and WHICH mutations refresh them — it adds no polling and changes no
 * response contract.
 *
 * keepUnusedDataFor (RTK default 60 s) is raised to LOW_CHURN_CACHE_SECONDS for
 * reads that change only when the user does something specific:
 *   onboarding profile, assessment path (+ legacy next-step), gamification
 *   overview, assessment stats, public-profile settings, server capabilities.
 * Server capabilities (GET /capabilities) are read on every fresh load by the
 * shell menu's gating (useStaticMenuQuery) and by several pages; they all use
 * the one getCapabilities cache entry (arg undefined), so a load makes ONE
 * request however many components read it. They change only with a deploy or
 * a flag/entitlement change, which Central events invalidate ('Capabilities').
 * Navigating away from the dashboard and back within that window refetches
 * none of them. Recent practice sessions keep the default 60 s.
 *
 * Correctness comes from tags, not from the timer. Every mutation that changes
 * what these reads show invalidates them:
 *   - already, in their own slices: onboarding save/steps ('User',
 *     'SkillProgress'), assessments ('Assessments'), public-profile update
 *     ('PublicProfile'), credentials ('User');
 *   - added here: ending or finalizing a practice / coaching / NPC session or
 *     an assessment, and completing an exam, invalidate 'DashboardSummary',
 *     which the overview, stats and path reads now also provide — XP,
 *     authority and path progress move when a session completes.
 * A workspace switch or logout resets the whole API cache (useWorkspace,
 * useCentralAuth), so nothing outlives the workspace it was read in.
 *
 * Tag types are added with enhanceEndpoints from this module only; the shared
 * api.ts tag list is not edited.
 */

import { api } from '../api';
// The endpoints enhanced below must be injected first.
import './onboardingApi';
import './gamificationApi';
import './assessmentApi';
import './portfolioApi';
import './practiceApi';
import './coachingApi';
import './npcApi';
import './credentialsApi';
import './capabilitiesApi';

export const LOW_CHURN_CACHE_SECONDS = 300;
export const DASHBOARD_SUMMARY_TAG = 'DashboardSummary';

/** Reads cached for LOW_CHURN_CACHE_SECONDS. */
export const LOW_CHURN_QUERIES = [
  'getOnboardingProfile',
  'getAssessmentPath',
  'getNextStep',
  'getMyGamificationOverview',
  'getAssessmentStats',
  'getMyPublicProfileSettings',
  'getCapabilities',
] as const;

/** Reads that also provide DASHBOARD_SUMMARY_TAG. */
export const DASHBOARD_SUMMARY_QUERIES = [
  // Carries the assessment path (?include=path) — the dashboard's path card.
  'getOnboardingProfile',
  'getAssessmentPath',
  'getNextStep',
  'getMyGamificationOverview',
  'getAssessmentStats',
] as const;

/** Mutations that complete work and therefore also invalidate DASHBOARD_SUMMARY_TAG. */
export const DASHBOARD_SUMMARY_MUTATIONS = [
  'endPracticeSession',
  'finalizePracticeSession',
  'endCoachingSession',
  'finalizeCoachingSession',
  'finalizeNpcSession',
  'finalizeAssessment',
  'abandonAssessment',
  'completeExam',
  // A completed lesson moves the streak and the overview.
  'completeLesson',
] as const;

// The injected endpoints are not part of the base api's static type, so the
// definitions are touched through this narrow structural view.
type TagList = unknown[];
type TagsField = TagList | ((...args: unknown[]) => TagList) | undefined;
interface MutableDefinition {
  keepUnusedDataFor?: number;
  providesTags?: TagsField;
  invalidatesTags?: TagsField;
}
type Enhancer = (definition: MutableDefinition) => void;
interface Enhanceable {
  enhanceEndpoints(options: {
    addTagTypes?: readonly string[];
    endpoints?: Record<string, Enhancer>;
  }): unknown;
}

function withTag(field: TagsField): TagsField {
  return (...args: unknown[]) => {
    const base = typeof field === 'function' ? field(...args) : field ?? [];
    return [...base, DASHBOARD_SUMMARY_TAG];
  };
}

const endpoints: Record<string, Enhancer> = {};
const add = (name: string, fn: Enhancer) => {
  const prev = endpoints[name];
  // A missing definition is skipped rather than crashing the app; the
  // request-budget test asserts every name above exists.
  endpoints[name] = (d) => {
    if (!d) return;
    prev?.(d);
    fn(d);
  };
};

for (const name of LOW_CHURN_QUERIES) {
  add(name, (d) => { d.keepUnusedDataFor = LOW_CHURN_CACHE_SECONDS; });
}
for (const name of DASHBOARD_SUMMARY_QUERIES) {
  add(name, (d) => { d.providesTags = withTag(d.providesTags); });
}
for (const name of DASHBOARD_SUMMARY_MUTATIONS) {
  add(name, (d) => { d.invalidatesTags = withTag(d.invalidatesTags); });
}

(api as unknown as Enhanceable).enhanceEndpoints({
  addTagTypes: [DASHBOARD_SUMMARY_TAG],
  endpoints,
});
